/**
 * Typed API client for Drive — folders, files, versions, trash, shares.
 * Mirrors internal/drive. File bytes live in the attachments subsystem; the
 * in-app upload endpoints (uploadFile / uploadVersion) store the bytes and the
 * drive metadata atomically. createFile remains for callers that already hold
 * an attachment id.
 */

import { apiClient } from '@/lib/apiClient';

export interface DriveFolder {
  id: string;
  parentId: string | null;
  name: string;
  ownerUserId: string | null;
  createdAt: string;
}

export interface DriveFile {
  id: string;
  folderId: string | null;
  name: string;
  currentAttachmentId: string | null;
  sizeBytes: number | null;
  mimeType: string | null;
  ownerUserId: string | null;
  createdAt: string;
}

export interface DriveFileVersion {
  id: string;
  version: number;
  attachmentId: string;
  sizeBytes: number | null;
  uploadedAt: string;
}

export interface DriveShare {
  id: string;
  folderId: string | null;
  fileId: string | null;
  targetType: string;
  targetId: string | null;
  permission: string;
  createdAt: string;
}

export interface TrashContents {
  folders: DriveFolder[];
  files: DriveFile[];
}

export async function listFolders(parentId?: string): Promise<DriveFolder[]> {
  const { data } = await apiClient.get<DriveFolder[]>('/drive/folders', {
    params: parentId ? { parentId } : {},
  });
  return data;
}

export async function createFolder(name: string, parentId?: string): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/drive/folders', { name, parentId });
  return data;
}

export async function trashFolder(id: string): Promise<void> {
  await apiClient.delete(`/drive/folders/${id}`);
}

export async function restoreFolder(id: string): Promise<void> {
  await apiClient.post(`/drive/folders/${id}/restore`, {});
}

export async function listFiles(folderId?: string): Promise<DriveFile[]> {
  const { data } = await apiClient.get<DriveFile[]>('/drive/files', {
    params: folderId ? { folderId } : {},
  });
  return data;
}

export interface CreateFileRequest {
  folderId?: string;
  name: string;
  attachmentId: string;
  sizeBytes?: number;
  mimeType?: string;
}

export async function createFile(req: CreateFileRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/drive/files', req);
  return data;
}

/** Upload a new file (multipart). Stores the bytes + creates the drive file. */
export async function uploadFile(file: File, folderId?: string, name?: string): Promise<DriveFile> {
  const fd = new FormData();
  fd.append('file', file);
  if (folderId) fd.append('folderId', folderId);
  if (name) fd.append('name', name);
  const { data } = await apiClient.post<DriveFile>('/drive/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Upload a new version of an existing file (multipart). */
export async function uploadVersion(
  fileId: string,
  file: File,
): Promise<{ version: number; attachmentId: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await apiClient.post<{ version: number; attachmentId: string }>(
    `/drive/files/${fileId}/versions/upload`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function listVersions(fileId: string): Promise<DriveFileVersion[]> {
  const { data } = await apiClient.get<DriveFileVersion[]>(`/drive/files/${fileId}/versions`);
  return data;
}

export async function trashFile(id: string): Promise<void> {
  await apiClient.delete(`/drive/files/${id}`);
}

export async function restoreFile(id: string): Promise<void> {
  await apiClient.post(`/drive/files/${id}/restore`, {});
}

export async function listTrash(): Promise<TrashContents> {
  const { data } = await apiClient.get<TrashContents>('/drive/trash');
  return data;
}

export async function listShares(folderId?: string, fileId?: string): Promise<DriveShare[]> {
  const params: Record<string, string> = {};
  if (folderId) params.folderId = folderId;
  if (fileId) params.fileId = fileId;
  const { data } = await apiClient.get<DriveShare[]>('/drive/shares', { params });
  return data;
}

export interface CreateShareRequest {
  folderId?: string;
  fileId?: string;
  targetType: string;
  targetId?: string;
  permission: string;
}

export async function createShare(req: CreateShareRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/drive/shares', req);
  return data;
}

export interface PublicLink {
  token: string;
  url: string;
  expiresAt: string;
}

// Mint (or rotate) a 72h public share link for a folder/file (131.34). The
// returned url points at the unauthenticated viewer (/p/drive/...).
export async function mintFolderPublicLink(id: string): Promise<PublicLink> {
  const { data } = await apiClient.post<PublicLink>(`/drive/folders/${id}/public-link`);
  return data;
}

export async function mintFilePublicLink(id: string): Promise<PublicLink> {
  const { data } = await apiClient.post<PublicLink>(`/drive/files/${id}/public-link`);
  return data;
}
